/**
 * Wallet model for the demo: the BUYER (agent) wallet with its spend budget, and a
 * per-SELLER receiving wallet. The seller exposes /balance (a live Base Sepolia read of
 * the agent wallet, done server-side); the discovery doc carries each seller's payTo.
 * Both locally (Python seller) and deployed (seller-content Lambda), /balance is served
 * from the same-origin seller base, so the client just fetches it.
 */

/** Read the agent (buyer) wallet's on-chain balance via the seller's /balance endpoint. */
export async function fetchBuyerWallet(cfg) {
  try {
    const r = await fetch(`${cfg.sellerBrowserBase}/balance`, { method: 'GET' });
    if (!r.ok) return null;
    const d = await r.json();
    return { address: d.address || null, usdc: d.usdc ?? null, eth: d.eth ?? null };
  } catch {
    return null;
  }
}

/** Pull the per-seller receiving wallets from the discovery doc (payTo on each item). */
export async function fetchSellerWallets(sellerBrowserBase) {
  try {
    const r = await fetch(`${sellerBrowserBase}/discovery/resources`, { method: 'GET' });
    if (!r.ok) return [];
    const doc = await r.json();
    const items = Array.isArray(doc.items) ? doc.items : [];
    // One wallet per distinct seller (flowId/serviceName), from the first accept's payTo.
    const bySeller = new Map();
    for (const it of items) {
      const demo = (it.extensions && it.extensions.x402demo) || {};
      const id = demo.flowId || it.serviceName || 'seller';
      const accept = (it.accepts && it.accepts[0]) || {};
      if (!bySeller.has(id)) {
        bySeller.set(id, {
          sellerId: id,
          name: it.serviceName || id,
          payTo: accept.payTo || null,
          network: accept.network || null,
        });
      }
    }
    return [...bySeller.values()];
  } catch {
    return [];
  }
}

/**
 * Pre-fetch the discovery doc and shape it like the agent's `discovered` event, so
 * the Discovery tab shows the payable skills on load (before any chat turn). The
 * agent's own `discovered` event later replaces this with what IT discovered.
 */
export async function fetchDiscovery(sellerBrowserBase) {
  try {
    const r = await fetch(`${sellerBrowserBase}/discovery/resources`);
    if (!r.ok) return null;
    const doc = await r.json();
    const items = Array.isArray(doc.items) ? doc.items : [];
    const bySeller = new Map();
    for (const it of items) {
      const demo = (it.extensions && it.extensions.x402demo) || {};
      const id = demo.flowId || it.serviceName || 'seller';
      const accept = (it.accepts && it.accepts[0]) || {};
      if (!bySeller.has(id)) {
        bySeller.set(id, {
          sellerId: id,
          serviceName: it.serviceName || id,
          baseUrl: '',
          network: accept.network || null,
          source: 'x402demo',
          skills: [],
        });
      }
      // Pull an example value from the bazaar input (e.g. {topic:"microwave"}) so the
      // UI can derive natural starter prompts from what each seller actually offers.
      const input = ((it.extensions && it.extensions.bazaar && it.extensions.bazaar.info) || {}).input || {};
      const exParams = input.pathParams || input.queryParams || {};
      const example = Object.values(exParams)[0] || '';
      bySeller.get(id).skills.push({
        skillId: demo.skillId || it.routeTemplate || 'skill',
        name: demo.skillName || it.serviceName || demo.skillId,
        style: demo.style || 'traditional',
        amount: accept.amount || null,
        example: String(example || ''),
        // Seller-authored suggestion chip ({flow,label,prompt}) — the UI renders it
        // verbatim; nothing about the phrasing is hardcoded in the frontend.
        suggestion: demo.suggestion || null,
      });
    }
    const sellers = [...bySeller.values()];
    return sellers.length ? { usePortal: true, mode: 'preview', sellers } : null;
  } catch {
    return null;
  }
}

/**
 * Merge the agent's per-turn `discovered` event into the existing discovery snapshot,
 * UNION by sellerId — so the seller tabs never shrink mid-session. The agent's event
 * can legitimately report a SUBSET (only the sellers it touched this turn); replacing
 * with that subset would make the other storefront tabs disappear. We keep every
 * seller ever seen, preferring the richer (more-skills) entry on a collision.
 */
export function mergeDiscovery(base, incoming) {
  if (!incoming) return base;
  if (!base || !(base.sellers || []).length) return incoming;
  const bySeller = new Map();
  for (const s of base.sellers || []) bySeller.set(s.sellerId, s);
  for (const s of incoming.sellers || []) {
    const existing = bySeller.get(s.sellerId);
    if (!existing || (s.skills?.length || 0) >= (existing.skills?.length || 0)) {
      bySeller.set(s.sellerId, { ...existing, ...s });
    }
  }
  return {
    usePortal: incoming.usePortal ?? base.usePortal,
    mode: incoming.mode ?? base.mode,
    sellers: [...bySeller.values()],
  };
}

/** Short 0x… display form. */
export function shortAddr(a) {
  if (!a) return '—';
  return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}
